import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import config from '../config';
import { getOrganizationContext } from '../orgContext';
import {
  AdminDataGrid,
  AdminFilterBar,
  AdminPageHeader,
  DataGridColumn,
  DataGridRowAction,
  FilterConfig,
  HeaderAction,
  SortDirection,
} from './components/AdminMasterDetail';
import {
  AdminBreadcrumbs,
  AdminSectionHeading,
  AdminStatCard,
  ConfirmDialog,
} from './components/AdminUiPrimitives';
import {
  IconArchive,
  IconBook,
  IconDelete,
  IconGeneral,
  IconMetadata,
  IconRelationships,
  IconSpark,
} from './components/AdminIcons';

interface MyOrganization {
  organizationId: number;
  organizationName: string;
  organizationSlug: string;
  isDefault: boolean;
  roles: string[];
}

interface OrganizationSummary {
  organizationId: number;
  name: string;
  slug: string;
}

interface OrganizationMember {
  membershipId: number;
  userId: number;
  auth0Sub: string;
  email: string;
  status: string;
  isDefault: boolean;
  roles: string[];
}

const ROLE_OPTIONS = ['Owner', 'Principal', 'Teacher', 'Parent', 'Student'];

const hasOwnerRole = (roles: string[]): boolean => roles.some(role => role.trim().toLowerCase() === 'owner');

type SortKey = 'member' | 'status' | 'roles';

interface MembershipRoleRow {
  id: number;
  member: string;
  status: string;
  roles: string;
  membership: OrganizationMember;
}

const DEFAULT_PAGE_SIZE = 10;

const ManageOrganizationMembershipRoles: React.FC = () => {
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();
  const apiBaseUrl = `${config.apiBaseUrl}/api/organizations`;

  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [myOrganizations, setMyOrganizations] = useState<MyOrganization[]>([]);
  const [allOrganizations, setAllOrganizations] = useState<OrganizationSummary[]>([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [selectedRoleMembershipId, setSelectedRoleMembershipId] = useState<number>(0);
  const [selectedRoleKey, setSelectedRoleKey] = useState<string>('Teacher');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState<SortKey>('member');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [mobilePane, setMobilePane] = useState<'list' | 'details'>('list');
  const [pendingRemove, setPendingRemove] = useState<{ membershipId: number; roleKey: string } | null>(null);

  const isSmallScreen = viewportWidth < 768;
  const isNarrowLayout = viewportWidth < 1140;

  const activeOrganizationContext = useMemo(() => getOrganizationContext(), []);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const requestedOrganizationId = useMemo(() => {
    const value = searchParams.get('organizationId');
    const parsed = value ? Number(value) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);

  const requestedMembershipId = useMemo(() => {
    const value = searchParams.get('membershipId');
    const parsed = value ? Number(value) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);

  const requestedUserId = useMemo(() => {
    const value = searchParams.get('userId');
    const parsed = value ? Number(value) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);

  const clearMessages = () => {
    setError('');
    setNotice('');
  };

  const getManagementToken = useCallback(async (): Promise<string> => {
    const tokenResponse = await getAccessTokenSilently().catch(() =>
      getAccessTokenSilently({ cacheMode: 'off' } as any)
    );

    const token =
      typeof tokenResponse === 'string'
        ? tokenResponse
        : (tokenResponse as { access_token?: string })?.access_token;

    if (!token) {
      throw new Error('Unable to get access token. Please sign in again and retry.');
    }

    return token;
  }, [getAccessTokenSilently]);

  const withAuth = useCallback(
    async (request: AxiosRequestConfig) => {
      const token = await getManagementToken();
      return axios({
        ...request,
        headers: {
          ...(request.headers ?? {}),
          Authorization: `Bearer ${token}`
        }
      });
    },
    [getManagementToken]
  );

  const organizationOptions = useMemo(
    () => {
      if (isSuperAdmin && allOrganizations.length > 0) {
        return allOrganizations.map(o => ({ id: o.organizationId, name: o.name, slug: o.slug }));
      }

      return myOrganizations
        .filter(o => hasOwnerRole(o.roles))
        .map(o => ({ id: o.organizationId, name: o.organizationName, slug: o.organizationSlug }));
    },
    [allOrganizations, isSuperAdmin, myOrganizations]
  );

  const selectedOrganization = useMemo(
    () => organizationOptions.find(org => org.id === selectedOrganizationId) ?? null,
    [organizationOptions, selectedOrganizationId]
  );

  const loadOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      clearMessages();

      const myResponse = await withAuth({ method: 'get', url: `${apiBaseUrl}/my` });
      const platformRolesResponse = await withAuth({ method: 'get', url: `${apiBaseUrl}/platform-user-roles` }).catch(() => null);
      const canUseSuperAdminTools = !!platformRolesResponse;
      const allResponse = canUseSuperAdminTools
        ? await withAuth({ method: 'get', url: `${apiBaseUrl}/discover` }).catch(() => null)
        : null;

      const myOrgs = (myResponse.data ?? []) as MyOrganization[];
      const allOrgs = (allResponse?.data ?? []) as OrganizationSummary[];

      setMyOrganizations(myOrgs);
      setAllOrganizations(allOrgs);
      setIsSuperAdmin(canUseSuperAdminTools);

      const ownerOrganizations = myOrgs.filter(o => hasOwnerRole(o.roles));
      const activeOrganizationId = activeOrganizationContext?.organizationId ?? null;
      const defaultMyOrg = ownerOrganizations.find(o => o.organizationId === activeOrganizationId)?.organizationId
        ?? ownerOrganizations.find(o => o.isDefault)?.organizationId
        ?? ownerOrganizations[0]?.organizationId;
      const defaultAllOrg = allOrgs[0]?.organizationId;
      const resolved = canUseSuperAdminTools
        ? (requestedOrganizationId ?? activeOrganizationId ?? defaultAllOrg ?? defaultMyOrg ?? null)
        : (activeOrganizationId ?? requestedOrganizationId ?? defaultMyOrg ?? null);

      setSelectedOrganizationId(prev => prev ?? resolved);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to load organizations.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [activeOrganizationContext?.organizationId, apiBaseUrl, requestedOrganizationId, withAuth]);

  const loadMembers = useCallback(
    async (organizationId: number) => {
      try {
        setLoading(true);
        clearMessages();

        const response = await withAuth({ method: 'get', url: `${apiBaseUrl}/${organizationId}/members` });
        setMembers((response.data ?? []) as OrganizationMember[]);
      } catch (err: any) {
        const message = err?.response?.data?.message || err?.message || 'Failed to load memberships.';
        setError(message);
        setMembers([]);
      } finally {
        setLoading(false);
      }
    },
    [apiBaseUrl, withAuth]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    loadOrganizations();
  }, [isAuthenticated, loadOrganizations]);

  useEffect(() => {
    if (!requestedOrganizationId || !isSuperAdmin) {
      return;
    }

    setSelectedOrganizationId(requestedOrganizationId);
  }, [isSuperAdmin, requestedOrganizationId]);

  useEffect(() => {
    if (isSuperAdmin) {
      return;
    }

    if (activeOrganizationContext?.organizationId) {
      setSelectedOrganizationId(activeOrganizationContext.organizationId);
    }
  }, [activeOrganizationContext?.organizationId, isSuperAdmin]);

  useEffect(() => {
    if (!isAuthenticated || !selectedOrganizationId) {
      setMembers([]);
      return;
    }

    loadMembers(selectedOrganizationId);
  }, [isAuthenticated, selectedOrganizationId, loadMembers]);

  useEffect(() => {
    if (!members.length) {
      return;
    }

    const matchedMembership = requestedMembershipId
      ? members.find(m => m.membershipId === requestedMembershipId)
      : null;

    const matchedUserMembership = requestedUserId
      ? members.find(m => m.userId === requestedUserId)
      : null;

    const targetMembership = matchedMembership ?? matchedUserMembership;

    if (targetMembership) {
      setSelectedRoleMembershipId(targetMembership.membershipId);
    }
  }, [members, requestedMembershipId, requestedUserId]);

  const filteredMembers = useMemo(() => {
    if (!requestedUserId) {
      return members;
    }

    return members.filter(member => member.userId === requestedUserId);
  }, [members, requestedUserId]);

  const rows = useMemo<MembershipRoleRow[]>(
    () =>
      filteredMembers.map(member => ({
        id: member.membershipId,
        member: member.email || member.auth0Sub || '-',
        status: member.status,
        roles: member.roles.join(', ') || 'None',
        membership: member,
      })),
    [filteredMembers]
  );

  const filteredRows = useMemo(() => {
    const query = searchValue.trim().toLowerCase();

    return rows.filter(row => {
      if (query) {
        const haystack = [
          row.member,
          row.membership.auth0Sub,
          row.roles,
          row.status,
          row.membership.membershipId,
          row.membership.userId,
        ]
          .join(' ')
          .toLowerCase();

        if (!haystack.includes(query)) {
          return false;
        }
      }

      if (statusFilter === 'all') {
        return true;
      }

      if (statusFilter === 'default') {
        return row.membership.isDefault;
      }

      return row.status.toLowerCase() === statusFilter;
    });
  }, [rows, searchValue, statusFilter]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    const direction = sortDirection === 'asc' ? 1 : -1;

    sorted.sort((first, second) => {
      const left = String(first[sortKey]).toLowerCase();
      const right = String(second[sortKey]).toLowerCase();

      if (left < right) {
        return -1 * direction;
      }
      if (left > right) {
        return 1 * direction;
      }
      return 0;
    });

    return sorted;
  }, [filteredRows, sortDirection, sortKey]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / DEFAULT_PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * DEFAULT_PAGE_SIZE;
    return sortedRows.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [page, sortedRows]);

  const selectedMembership = useMemo(
    () => filteredMembers.find(m => m.membershipId === selectedRoleMembershipId) ?? null,
    [filteredMembers, selectedRoleMembershipId]
  );

  const handleAssignRole = async () => {
    if (!selectedOrganizationId || !selectedRoleMembershipId || !selectedRoleKey) {
      setError('Membership and role are required.');
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      await withAuth({
        method: 'post',
        url: `${apiBaseUrl}/${selectedOrganizationId}/members/${selectedRoleMembershipId}/roles`,
        data: { roleKey: selectedRoleKey }
      });

      setNotice('Role assigned.');
      await loadMembers(selectedOrganizationId);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to assign role.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveRole = async (membershipId: number, roleKey: string) => {
    if (!selectedOrganizationId) {
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      await withAuth({
        method: 'delete',
        url: `${apiBaseUrl}/${selectedOrganizationId}/members/${membershipId}/roles/${encodeURIComponent(roleKey)}`
      });

      setNotice('Role removed.');
      await loadMembers(selectedOrganizationId);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to remove role.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSort = (columnKey: string) => {
    if (!['member', 'status', 'roles'].includes(columnKey)) {
      return;
    }

    const nextKey = columnKey as SortKey;
    if (sortKey === nextKey) {
      setSortDirection(current => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(nextKey);
    setSortDirection('asc');
  };

  const columns = useMemo<DataGridColumn<MembershipRoleRow>[]>(() => {
    const items: DataGridColumn<MembershipRoleRow>[] = [
      {
        key: 'member',
        label: 'Member',
        sortable: true,
        width: isSmallScreen ? '58%' : '35%',
        render: (row) => (
          <div style={{ minWidth: isSmallScreen ? 0 : 180 }}>
            <div style={{ fontWeight: 700, color: '#142541', overflowWrap: 'anywhere' }}>{row.member}</div>
            <div style={{ fontSize: '0.75rem', color: '#607399', marginTop: 2 }}>#{row.membership.membershipId} • User #{row.membership.userId}</div>
          </div>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        width: isSmallScreen ? '20%' : '16%',
        render: (row) => (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: 999,
              border: '1px solid #d1ddf3',
              color: '#3a5686',
              background: '#f4f8ff',
              padding: '2px 8px',
              fontSize: '0.73rem',
              fontWeight: 700,
            }}
          >
            {row.status}
          </span>
        ),
      },
    ];

    if (!isSmallScreen) {
      items.push({
        key: 'roles',
        label: 'Roles',
        sortable: true,
        width: '34%',
        render: (row) => row.roles,
      });
    }

    return items;
  }, [isSmallScreen]);

  const rowActions: DataGridRowAction<MembershipRoleRow>[] = [
    {
      key: 'manage',
      label: 'Manage roles',
      icon: <IconRelationships size={14} />,
      onClick: (row) => {
        setSelectedRoleMembershipId(row.membership.membershipId);
        if (isSmallScreen) {
          setMobilePane('details');
        }
      },
      isDisabled: () => saving,
    },
  ];

  const filterConfigs = useMemo<FilterConfig[]>(() => {
    const filters: FilterConfig[] = [];

    if (isSuperAdmin) {
      filters.push({
        key: 'org',
        label: 'Organization',
        value: selectedOrganizationId ? String(selectedOrganizationId) : '',
        onChange: (value) => {
          setSelectedOrganizationId(value ? Number(value) : null);
          setSelectedRoleMembershipId(0);
          setSelectedIds([]);
          setPage(1);
          if (isSmallScreen) {
            setMobilePane('list');
          }
        },
        options: [
          { value: '', label: 'Select organization' },
          ...organizationOptions.map(org => ({
            value: String(org.id),
            label: `${org.name} (${org.slug})`,
          })),
        ],
      });
    }

    filters.push({
      key: 'status',
      label: 'Status',
      value: statusFilter,
      onChange: (value) => {
        setStatusFilter(value);
        setPage(1);
      },
      options: [
        { value: 'all', label: 'All statuses' },
        { value: 'active', label: 'Active' },
        { value: 'invited', label: 'Invited' },
        { value: 'suspended', label: 'Suspended' },
        { value: 'left', label: 'Left' },
        { value: 'default', label: 'Default only' },
      ],
    });

    return filters;
  }, [isSmallScreen, isSuperAdmin, organizationOptions, selectedOrganizationId, statusFilter]);

  const actions: HeaderAction[] = [
    {
      key: 'refresh',
      label: 'Refresh',
      variant: 'secondary',
      onClick: () => {
        void loadOrganizations();
        if (selectedOrganizationId) {
          void loadMembers(selectedOrganizationId);
        }
      },
      disabled: saving,
    },
  ];

  const totalMembers = filteredMembers.length;
  const membersWithRoles = filteredMembers.filter(member => member.roles.length > 0).length;
  const roleAssignments = filteredMembers.reduce((sum, member) => sum + member.roles.length, 0);
  const noRoleMembers = filteredMembers.filter(member => member.roles.length === 0).length;

  const showListPanel = !isSmallScreen || mobilePane === 'list';
  const showDetailsPanel = !isSmallScreen || mobilePane === 'details';

  if (!isAuthenticated) {
    return (
      <div style={{ maxWidth: 720, margin: '32px auto', padding: 16 }}>
        <h2 style={{ marginBottom: 12 }}>Organization Membership Roles</h2>
        <p style={{ marginBottom: 16 }}>Please sign in to manage membership roles.</p>
        <button
          onClick={() => loginWithRedirect()}
          style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 16px', cursor: 'pointer', fontWeight: 600 }}
        >
          Sign In
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', minHeight: 'calc(100vh - 260px)', display: 'grid', gap: 12 }}>
      <AdminBreadcrumbs
        items={[
          { label: 'Administration', to: '/analytics' },
          { label: 'Organizations', to: '/manage-organizations' },
          { label: 'Memberships', to: '/manage-organization-memberships' },
          {
            label: 'Membership Roles',
            onClick: () => {
              if (isSmallScreen) {
                setMobilePane('list');
              }
            },
          },
          {
            label: selectedOrganization ? `${selectedOrganization.name} Roles` : 'Membership Roles',
            isCurrent: true,
          },
        ]}
      />

      <AdminPageHeader
        title="Organization Membership Roles"
        subtitle={`${totalMembers} memberships • ${roleAssignments} role assignments`}
        actions={actions}
      />

      {(error || notice) && (
        <div
          style={{
            borderRadius: 10,
            border: `1px solid ${error ? '#fecaca' : '#bfdbfe'}`,
            background: error ? '#fff1f2' : '#eff6ff',
            color: error ? '#b91c1c' : '#1d4ed8',
            padding: '10px 12px',
            fontSize: '0.84rem',
          }}
        >
          {error || notice}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 10,
        }}
      >
        <AdminStatCard label="Memberships" value={totalMembers} icon={<IconBook size={15} />} />
        <AdminStatCard label="With Roles" value={membersWithRoles} icon={<IconRelationships size={15} />} accent="active" />
        <AdminStatCard label="Assignments" value={roleAssignments} icon={<IconSpark size={15} />} accent="draft" />
        <AdminStatCard label="No Roles" value={noRoleMembers} icon={<IconArchive size={15} />} accent="archived" />
      </div>

      <AdminFilterBar
        searchValue={searchValue}
        onSearchChange={(value) => {
          setSearchValue(value);
          setPage(1);
        }}
        searchPlaceholder="Search by member, status, roles, auth0 id..."
        filters={filterConfigs}
      />

      {isSmallScreen && (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            border: '1px solid #d8e3f6',
            borderRadius: 11,
            background: '#f6f9ff',
            padding: 4,
            width: 'fit-content',
          }}
        >
          <button
            type="button"
            className={`amd-btn ${mobilePane === 'list' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
            onClick={() => setMobilePane('list')}
          >
            List
          </button>
          <button
            type="button"
            className={`amd-btn ${mobilePane === 'details' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
            onClick={() => setMobilePane('details')}
          >
            Details
          </button>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isNarrowLayout ? '1fr' : 'minmax(420px, 58%) minmax(320px, 42%)',
          gap: 12,
          minHeight: isNarrowLayout ? 'auto' : 560,
        }}
      >
        {showListPanel && (
          <div style={{ minHeight: 0 }}>
            {loading ? (
              <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, padding: 22, background: '#fff' }}>
                Loading memberships...
              </div>
            ) : (
              <AdminDataGrid<MembershipRoleRow>
                rows={pagedRows}
                columns={columns}
                selectedRowId={selectedRoleMembershipId || null}
                onRowClick={(row) => {
                  setSelectedRoleMembershipId(row.membership.membershipId);
                  if (isSmallScreen) {
                    setMobilePane('details');
                  }
                }}
                selectedIds={selectedIds}
                onSelectedIdsChange={setSelectedIds}
                showSelection={!isSmallScreen}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSort={handleSort}
                page={page}
                pageSize={DEFAULT_PAGE_SIZE}
                totalCount={sortedRows.length}
                onPageChange={setPage}
                emptyMessage={selectedOrganizationId ? 'No memberships found for this organization context.' : 'Select an organization to load memberships.'}
                rowActions={rowActions}
              />
            )}
          </div>
        )}

        {showDetailsPanel && (
          <div
            style={{
              border: '1px solid #dfe8f8',
              borderRadius: 12,
              background: '#fff',
              boxShadow: '0 8px 18px rgba(18, 42, 80, 0.08)',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid #edf2fb' }}>
              {isSmallScreen && (
                <button
                  type="button"
                  className="amd-btn amd-btn-ghost"
                  style={{ marginBottom: 8 }}
                  onClick={() => setMobilePane('list')}
                >
                  Back to List
                </button>
              )}
              <h3
                style={{
                  margin: 0,
                  width: '100%',
                  boxSizing: 'border-box',
                  fontSize: '1.02rem',
                  fontWeight: 800,
                  color: '#173159',
                  background: selectedMembership
                    ? 'linear-gradient(180deg, #f5f9ff 0%, #ebf3ff 100%)'
                    : 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
                  border: selectedMembership ? '1px solid #cfe0fb' : '1px solid #d9e2ef',
                  borderRadius: 10,
                  padding: '9px 11px',
                }}
              >
                {selectedMembership
                  ? `Membership #${selectedMembership.membershipId}`
                  : selectedOrganization
                  ? `${selectedOrganization.name} Role Management`
                  : 'Select an organization'}
              </h3>
            </div>

            <div style={{ padding: 14, overflowY: 'auto', minHeight: 0 }}>
              <div style={{ display: 'grid', gap: 18 }}>
                <section style={{ display: 'grid', gap: 12, paddingBottom: 14, borderBottom: '1px solid #edf2fb' }}>
                  <AdminSectionHeading icon={<IconGeneral size={14} />} title="Context" />
                  {(requestedOrganizationId || requestedUserId || requestedMembershipId) && (
                    <div style={{ fontSize: '0.82rem', color: '#5b6f93' }}>
                      Opened from memberships page with query context.
                    </div>
                  )}
                  {!isSuperAdmin && selectedOrganization && (
                    <div style={{ fontSize: '0.82rem', color: '#5b6f93' }}>
                      Managing roles in owner context: <strong>{selectedOrganization.name}</strong> ({selectedOrganization.slug})
                    </div>
                  )}
                  {!selectedOrganization && (
                    <div style={{ fontSize: '0.82rem', color: '#9a5f11' }}>
                      Select organization to load memberships and assign roles.
                    </div>
                  )}
                </section>

                <section style={{ display: 'grid', gap: 12, paddingBottom: 14, borderBottom: '1px solid #edf2fb' }}>
                  <AdminSectionHeading icon={<IconSpark size={14} />} title="Assign Role" />
                  <div style={{ display: 'grid', gap: 8, gridTemplateColumns: isSmallScreen ? '1fr' : 'minmax(0, 1fr) minmax(0, 1fr) auto' }}>
                    <select
                      value={selectedRoleMembershipId}
                      onChange={(event) => setSelectedRoleMembershipId(Number(event.target.value))}
                    >
                      <option value={0}>Select membership</option>
                      {filteredMembers.map(member => (
                        <option key={member.membershipId} value={member.membershipId}>
                          #{member.membershipId} - {member.email || member.auth0Sub}
                        </option>
                      ))}
                    </select>

                    <select
                      value={selectedRoleKey}
                      onChange={(event) => setSelectedRoleKey(event.target.value)}
                    >
                      {ROLE_OPTIONS.map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>

                    <button
                      type="button"
                      className="amd-btn amd-btn-primary"
                      onClick={() => {
                        void handleAssignRole();
                      }}
                      disabled={saving || !selectedOrganizationId || !selectedRoleMembershipId}
                    >
                      Assign Role
                    </button>
                  </div>
                </section>

                <section style={{ display: 'grid', gap: 12 }}>
                  <AdminSectionHeading icon={<IconMetadata size={14} />} title="Assigned Roles" />
                  {!selectedMembership ? (
                    <div
                      style={{
                        minHeight: 150,
                        border: '1px dashed #c9d6ef',
                        borderRadius: 12,
                        background: '#f8fbff',
                        display: 'grid',
                        placeContent: 'center',
                        textAlign: 'center',
                        color: '#4f658f',
                        gap: 8,
                        padding: 16,
                      }}
                    >
                      <div style={{ color: '#35568a', display: 'inline-flex', justifySelf: 'center' }}><IconBook size={24} /></div>
                      <div style={{ fontWeight: 700 }}>Select a membership from the list</div>
                    </div>
                  ) : (
                    <>
                      {selectedMembership.roles.length === 0 ? (
                        <span style={{ color: '#6b7280', fontSize: '0.82rem' }}>No roles assigned.</span>
                      ) : (
                        <div
                          style={{
                            border: '1px solid #dfe8f8',
                            borderRadius: 10,
                            overflow: 'hidden',
                            background: '#fff',
                          }}
                        >
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: isSmallScreen ? '1.3fr 1fr auto' : '2fr 1fr auto',
                              gap: 8,
                              padding: '10px 12px',
                              borderBottom: '1px solid #edf2fb',
                              background: '#f7faff',
                              fontSize: '0.76rem',
                              fontWeight: 800,
                              color: '#425f8b',
                            }}
                          >
                            <span>Member</span>
                            <span>Role</span>
                            <span>Action</span>
                          </div>

                          {selectedMembership.roles.map(role => (
                            <div
                              key={`${selectedMembership.membershipId}-${role}`}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: isSmallScreen ? '1.3fr 1fr auto' : '2fr 1fr auto',
                                gap: 8,
                                alignItems: 'center',
                                padding: '10px 12px',
                                borderBottom: '1px solid #f1f5fc',
                                fontSize: '0.82rem',
                              }}
                            >
                              <span style={{ color: '#173159', overflowWrap: 'anywhere' }}>{selectedMembership.email || selectedMembership.auth0Sub || '-'}</span>
                              <span style={{ color: '#48648f', fontWeight: 700 }}>{role}</span>
                              <button
                                type="button"
                                className="amd-btn amd-btn-ghost"
                                onClick={() => setPendingRemove({ membershipId: selectedMembership.membershipId, roleKey: role })}
                                disabled={saving}
                                title="Remove role"
                              >
                                <IconDelete size={12} /> Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </section>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(pendingRemove)}
        title="Remove role"
        message={pendingRemove ? `Remove role "${pendingRemove.roleKey}" from membership #${pendingRemove.membershipId}?` : ''}
        onClose={() => {
          if (!saving) {
            setPendingRemove(null);
          }
        }}
        actions={[
          {
            label: 'Cancel',
            variant: 'default',
            onClick: () => setPendingRemove(null),
            disabled: saving,
          },
          {
            label: 'Remove',
            variant: 'destructive',
            disabled: saving || !pendingRemove,
            onClick: () => {
              if (!pendingRemove) {
                return;
              }

              void handleRemoveRole(pendingRemove.membershipId, pendingRemove.roleKey);
              setPendingRemove(null);
            },
          },
        ]}
      />
    </div>
  );
};

export default ManageOrganizationMembershipRoles;
